import { Alert, Button, Collapse, Group, Loader, Stack, Text, Title, toast } from '@common/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { com } from '@eidu/web'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import { usePermissionPolicy } from '../../io/usePermissionPolicies'
import {
  useCreatePermissionPolicy,
  useDeletePermissionPolicy,
  useModifyPermissionPolicy,
} from '../../io/usePermissionPolicyMutations'
import { requireNotUndefinedOrNull } from '../../util/require'
import PermissionError from '../entity/PermissionError'
import { PermissionWithId } from '../../components/input/PermissionsInput'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import PermissionPolicyForm from '../../components/layout/PermissionPolicyForm'
import EntityType = com.eidu.entity.type.EntityType
import permissionPolicyIdFromString = com.eidu.entity.permission.permissionPolicyIdFromString
import useHasGlobalPermissions from './useHasGlobalPermissions'
import GlobalAction = com.eidu.entity.permission.GlobalAction

type CreateOrEditPermissionPolicyPageContentProps = {
  entityTypes: readonly EntityType[]
  name: string
  setName: (name: string) => void
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  onSubmit: () => Promise<void>
  onDelete?: () => Promise<void>
  onCancel: () => void
  isEditMode: boolean
  canSave: boolean
}

const CreateOrEditPermissionPolicyPageContent = ({
  entityTypes,
  name,
  setName,
  selectedPermissions,
  setSelectedPermissions,
  onSubmit,
  onDelete,
  onCancel,
  isEditMode,
  canSave,
}: CreateOrEditPermissionPolicyPageContentProps) => (
  <Stack p="lg">
    <Group justify="space-between" gap="xs">
      <Title order={2}>{isEditMode ? 'Update policy' : 'New policy'}</Title>
      {isEditMode && onDelete && (
        <ConfirmationDialog
          title="Are you sure you want to delete this policy? This action cannot be undone."
          content={<Text>Delete</Text>}
          onConfirm={onDelete}
        />
      )}
    </Group>
    <div style={{ paddingBlock: 'var(--trellis-spacing-md)' }}>
      <PermissionPolicyForm
        entityTypes={entityTypes}
        name={name}
        setName={setName}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
      />
    </div>
    <Group justify="flex-end" gap="md" p="md">
      <Button variant="default" onClick={onCancel}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={!canSave}>
        Save
      </Button>
    </Group>
  </Stack>
)

const CreateOrEditPermissionPolicyPageRoute = ({ isEditMode }: { isEditMode: boolean }) => {
  const navigate = useNavigate()
  const params = useParams()
  const entityTypes = useSortedEntityTypes() ?? []

  const policyId = isEditMode ? permissionPolicyIdFromString(requireNotUndefinedOrNull(params.policyId)) : undefined
  const policyQuery = usePermissionPolicy(policyId)
  const createMutation = useCreatePermissionPolicy()
  const modifyMutation = useModifyPermissionPolicy()
  const deleteMutation = useDeletePermissionPolicy()

  const [name, setName] = useState<string>('')
  const [selectedPermissions, setSelectedPermissions] = useState<readonly PermissionWithId[]>([])

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  useEffect(() => {
    if (isEditMode && policyQuery.policy) {
      setName(policyQuery.policy.policy.name)
      setSelectedPermissions(
        [...policyQuery.policy.policy.permissions.asJsReadonlySetView()].map((permission) => ({
          id: crypto.randomUUID(),
          permission,
        }))
      )
    }
  }, [isEditMode, policyQuery.policy])

  const submit = useCallback(async () => {
    const trimmedName = name.trim()
    try {
      const permissions = selectedPermissions.map(({ permission }) => permission)
      if (isEditMode && policyId) {
        await modifyMutation.mutateAsync({ id: policyId, name: trimmedName, permissions })
        toast.positive(`Policy updated`)
      } else {
        await createMutation.mutateAsync({ name: trimmedName, permissions })
        toast.positive(`Policy created`)
      }
      navigate(-1)
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to ${isEditMode ? 'update' : 'create'} policy`)
    }
  }, [name, selectedPermissions, isEditMode, policyId, createMutation, modifyMutation])

  const onCancel = useCallback(() => navigate(-1), [navigate])

  const onDelete = useCallback(async () => {
    if (!isEditMode || !policyId) return
    try {
      await deleteMutation.mutateAsync(policyId)
      toast.positive(`Deleted policy`)
      navigate(-1)
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to delete policy`)
    }
  }, [isEditMode, policyId, deleteMutation])

  if (entityTypes.length === 0) return null

  const submitting = createMutation.isPending || modifyMutation.isPending || deleteMutation.isPending
  const loading = policyQuery.isLoading || submitting
  const canSave = name.trim().length > 0 && selectedPermissions.length > 0 && !submitting
  const error = policyQuery.error

  if (policyQuery.isLoading) return <Loader m="xl" />

  return (
    <Stack>
      <Collapse expanded={!!error}>
        <Alert variant="filled" severity="negative" m="md">
          <div>{String(error)}</div>
          <div>Please reload the page and try again.</div>
        </Alert>
      </Collapse>
      {!error && hasManagePermissionGrantsPermission && (
        <CreateOrEditPermissionPolicyPageContent
          entityTypes={entityTypes}
          name={name}
          setName={setName}
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          onSubmit={submit}
          onDelete={isEditMode ? onDelete : undefined}
          onCancel={onCancel}
          isEditMode={isEditMode}
          canSave={canSave}
        />
      )}
      {!loading && !hasManagePermissionGrantsPermission && (
        <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
      )}
    </Stack>
  )
}

export default CreateOrEditPermissionPolicyPageRoute
